import {
  getOutcomeValueAndLabel,
  type GradingOutcome,
} from "@repo/app-types/entities/outcome";
import type {
  OutcomeDefinition,
  PlaybookTree,
} from "@repo/app-types/entities/playbook";
import { getPlaybookNode } from "@repo/app-types/entities/playbook";
import {
  PlaybookTestResultStatus,
  type AdminApiPlaybookTestResult,
  type AdminApiPlaybookTestSpec,
  type AdminApiPlaybookTestSpecResult,
  type PlaybookTestResultOutput,
  type PlaybookTestSpecExpected,
} from "@repo/app-types/entities/teammateAgent";
import {
  getAccuracyClassificationForGradingValue,
  type FeedbackAccuracyData,
} from "@repo/app-types/helpers/feedbackHelpers";

// Re-export for backwards compatibility
export { getAccuracyClassificationForGradingValue };

export interface TestPassRateData {
  /** Number of tests that passed (no incorrect items) */
  passing: number;
  /** Number of completed tests */
  total: number;
  /** Total number of test specs (including those without completed results) */
  totalSpecs: number;
  /** Pass rate as a percentage (0-100) */
  percentage: number;
}

/**
 * Calculates the pass rate for playbook test results.
 * A test passes if all grading values match expected values (no incorrect items).
 */
export function calculateTestPassRate(
  testSpecsResults: AdminApiPlaybookTestSpecResult[],
  assessmentTree: PlaybookTree,
  totalSpecsCount: number,
): TestPassRateData | null {
  // Only count test specs that have completed results
  const completedResults = testSpecsResults.filter(
    (result) =>
      result.playbook_test_result?.status === PlaybookTestResultStatus.DONE &&
      result.playbook_test_result?.output,
  );

  if (completedResults.length === 0) {
    return null;
  }

  let passingCount = 0;

  completedResults.forEach((result) => {
    const testResult = result.playbook_test_result;
    if (!testResult?.output) return;

    const accuracyData = calculatePlaybookTestResultAccuracy(
      testResult.output,
      result.playbook_test_spec.expected,
      assessmentTree,
    );

    // A test passes if there are no incorrect items (correct or close only)
    if (accuracyData && accuracyData.incorrect === 0) {
      passingCount++;
    }
  });

  return {
    passing: passingCount,
    total: completedResults.length,
    totalSpecs: totalSpecsCount,
    percentage:
      completedResults.length > 0
        ? Math.round((passingCount / completedResults.length) * 100)
        : 0,
  };
}

/**
 * Calculates accuracy metrics for a playbook test result by comparing expected values to the simulated conversation grading.
 */
export function calculatePlaybookTestResultAccuracy(
  outputGrading: PlaybookTestResultOutput,
  expectedValues: PlaybookTestSpecExpected,
  assessmentTree: PlaybookTree,
): FeedbackAccuracyData {
  let correct = 0;
  let close = 0;
  let incorrect = 0;

  const expectedNodes = expectedValues.nodes ?? {};

  // Count criteria grading matches
  if (outputGrading.criteria_grading) {
    Object.entries(outputGrading.criteria_grading).forEach(
      ([nodeId, grading]) => {
        const expectedValue = expectedNodes[nodeId];
        if (
          grading &&
          expectedValue !== undefined &&
          grading.value !== undefined
        ) {
          // Find the node to get its grading_value_type
          const node = getPlaybookNode(nodeId, assessmentTree);
          if (node?.type === "criteria") {
            const status = getAccuracyClassificationForGradingValue(
              node.grading_value_type,
              grading.value,
              String(expectedValue),
              true,
            );

            if (status === "correct") {
              correct++;
            } else if (status === "close") {
              close++;
            } else if (status === "incorrect") {
              incorrect++;
            }
          }
        }
      },
    );
  }

  // Count section grading matches
  if (outputGrading.section_grading) {
    Object.entries(outputGrading.section_grading).forEach(
      ([nodeId, grading]) => {
        const expectedValue = expectedNodes[nodeId];
        if (
          grading &&
          expectedValue !== undefined &&
          grading.is_not_applicable !== undefined
        ) {
          const status = getAccuracyClassificationForGradingValue(
            "section",
            grading.is_not_applicable ? "N/A" : "applicable",
            String(expectedValue),
            true,
          );

          if (status === "correct") {
            correct++;
          } else if (status === "close") {
            close++;
          } else if (status === "incorrect") {
            incorrect++;
          }
        }
      },
    );
  }

  const total = correct + close + incorrect;
  const accuracy = total > 0 ? (correct / total) * 100 : 0;

  return { total, correct, close, incorrect, accuracy };
}

/**
 * Checks if the test result's outcome matches the expected outcome.
 * Returns true if:
 * - No expected outcome was defined (nothing to check)
 * - The expected and actual outcome values match
 */
export function checkOutcomeMatch(
  expectedOutcome: GradingOutcome | undefined,
  actualOutcome: GradingOutcome | undefined,
  outcomeDefinition: OutcomeDefinition,
): boolean {
  const { value: expectedValue } = getOutcomeValueAndLabel(
    expectedOutcome,
    outcomeDefinition,
  );
  const { value: actualValue } = getOutcomeValueAndLabel(
    actualOutcome,
    outcomeDefinition,
  );

  // No expected outcome = match (nothing to check), or values match
  return expectedValue === undefined || expectedValue === actualValue;
}

export type TestResultStatus =
  | "passing"
  | "failing"
  | "in_progress"
  | "not_run";

/**
 * Determines the status of a test result based on its completion state and outcome match.
 */
export function getTestResultStatus(
  spec: AdminApiPlaybookTestSpec,
  result: AdminApiPlaybookTestResult | undefined,
  outcomeDefinition: OutcomeDefinition | null | undefined,
): TestResultStatus {
  if (result == null) {
    return "not_run";
  }
  if (result.status === PlaybookTestResultStatus.IN_PROGRESS) {
    return "in_progress";
  }
  if (result.status === PlaybookTestResultStatus.ERROR) {
    return "failing";
  }

  // Test completed - check if outcome matches expected
  if (!outcomeDefinition) {
    return "passing"; // No outcome tracking, consider done = passing
  }

  const isMatch = checkOutcomeMatch(
    spec.expected.outcomes,
    result.output?.outcomes,
    outcomeDefinition,
  );

  return isMatch ? "passing" : "failing";
}
